import { indiaDay, outstandingSummary } from './outstanding';

export function createOutstandingSync({ owner, fetchRows, storage, onChange, onProgress = () => {}, now = () => new Date() }) {
  let stopped = false, running = false;
  let progress = { total: 0, completed: 0, failed: 0, currentId: null, active: false };
  const allowed = new Set(), queued = [], attempts = new Map();
  const entries = {};
  const controller = new AbortController();
  const key = id => `scpaul:outstanding:v2:${owner}:${id}`;
  const emit = () => { if (!stopped) { onChange({ ...entries }); onProgress({ ...progress }); } };
  const fresh = entry => entry?.rows && entry.updatedAt && indiaDay(new Date(entry.updatedAt)) === indiaDay(now());
  function load(id) {
    try {
      const entry = JSON.parse(storage.getItem(key(id)));
      if (!entry || !Array.isArray(entry.rows) || !Number.isFinite(Date.parse(entry.updatedAt)) || Date.parse(entry.updatedAt) > now().getTime()) return;
      outstandingSummary(entry.rows, now());
      // Cached rows are display data only; server authorization still scopes every fetch.
      if (entry.rows.some(row => String(row.FKCustomerID ?? row.CustomerID ?? row.customer_id) !== id)) return;
      entries[id] = { rows: entry.rows, updatedAt: entry.updatedAt, status: 'ready' };
    } catch { /* Invalid or inaccessible cache is fetched again. */ }
  }
  async function pump() {
    if (running || stopped) return;
    running = true;
    while (queued.length && !stopped) {
      const id = queued.shift();
      progress.currentId = id;
      entries[id] = { ...entries[id], status: 'syncing', error: null };
      attempts.set(id, now().getTime()); emit();
      try {
        const rows = await fetchRows(id, controller.signal);
        outstandingSummary(rows, now());
        if (stopped) break;
        const entry = { rows, updatedAt: now().toISOString(), status: 'ready' };
        try { storage.setItem(key(id), JSON.stringify({ rows, updatedAt: entry.updatedAt })); }
        catch { entry.cacheUnavailable = true; }
        entries[id] = entry;
      } catch (error) {
        if (stopped) break;
        progress.failed += 1;
        entries[id] = { ...entries[id], status: 'error', error: error.message || 'Sync failed' };
      }
      progress.completed += 1;
      progress.currentId = null;
      progress.active = queued.length > 0;
      emit();
    }
    running = false;
  }
  function enqueue(id, force = false) {
    id = String(id);
    if (stopped || !allowed.has(id) || queued.includes(id) || entries[id]?.status === 'syncing') return;
    if (!force && (fresh(entries[id]) || now().getTime() - (attempts.get(id) ?? -Infinity) < 300000)) return;
    if (!progress.active) progress = { total: 0, completed: 0, failed: 0, currentId: null, active: true };
    progress.total += 1;
    entries[id] = { ...entries[id], status: 'queued' };
    if (force) queued.unshift(id); else queued.push(id);
  }
  return {
    start(customers) {
      customers.forEach(customer => { const id = String(customer.customer_id); allowed.add(id); load(id); enqueue(id); });
      emit(); pump();
    },
    refresh(id) { enqueue(id, true); emit(); pump(); },
    refreshAll() { allowed.forEach(id => enqueue(id, true)); emit(); pump(); },
    poll() { allowed.forEach(id => enqueue(id)); emit(); pump(); },
    stop() { stopped = true; queued.length = 0; controller.abort(); },
  };
}

